// packages block
import { useEffect, useState, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import FileDownload from "js-file-download";
import { Spinner } from "reactstrap";
import { useLazyQuery, useMutation } from "@apollo/client";
// components block
import AddReferenceModal from "./AddReferenceModal";
import Loader from "../../../components/layout/Loader";
import ConfirmPopUp from "../../../components/ConfirmPopUp";
import {
  DownloadIcon,
  EyeIcon,
  PenIcon,
  RefBadge,
  RefContactCell,
  RefDateCell,
  RefEmptyRow,
  RefFooter,
  RefIconButton,
  RefNameCell,
  RefSectionHeader,
  RefTableHead,
  TrashIcon,
} from "./ReferenceTableParts";
// utils, constants and graphql block
import { AppContext } from "../../../context";
import { DELETE_CANDIDATE_REFERENCE, GET_CANDIDATE_REFERENCES } from "./gql";
import ViewReferenceModal from "./ViewReferenceModal";

const REFERENCE_FORM_COLUMNS = [
  "Reference",
  "Contact",
  "Status",
  "Updated",
  "Actions",
];

const FormStatusBadge = ({ status }: { status?: string | null }) => {
  if (status === "COMPLETED") return <RefBadge tone="active">Completed</RefBadge>;
  if (status === "PENDING") return <RefBadge tone="warn">Pending</RefBadge>;
  if (!status) return <RefBadge tone="neutral">Not Started</RefBadge>;
  return <RefBadge tone="neutral">{status}</RefBadge>;
};

const DownloadReference = ({ item }: any) => {
  const [loading, setLoading] = useState<boolean>(false);

  const handleDownload = async (item: {
    id: string;
    referenceName: string;
  }) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || "http://localhost:3001"
        }/candidate-pipelining-reference/downloadreference/${item?.id}`,
        {
          responseType: "blob",
        }
      );
      FileDownload(response.data, `${item?.referenceName}.pdf`);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Error while downloading file..");
    }
  };

  return (
    <RefIconButton
      label="Download"
      disabled={loading || item.status === "PENDING"}
      onClick={() => {
        handleDownload(item);
      }}
    >
      {loading ? <Spinner size="sm" /> : <DownloadIcon />}
    </RefIconButton>
  );
};

const ReferenceListing = ({ candidateId, candidate }: any) => {
  const { user, userRoles } = useContext(AppContext);
  const [modal, setModal] = useState(false);
  const [isView, setIsView] = useState(false);

  const page = parseInt("1");
  const [currentPage, setCurrentPage] = useState(page);
  const [limit, setLimit] = useState(10);
  const [deleteReferenceId, setDeleteReferenceId] = useState<string>();
  const [editReferenceId, setEditReferenceId] = useState<string>("");
  const [actionConfirm, setReferenceDeleteConfirm] = useState(false);
  const [editPermissions, setEditPermissions] = useState(true);
  const [referenceData, setReferenceData] = useState({});

  const [deleteReference] = useMutation(DELETE_CANDIDATE_REFERENCE);
  const [getReferences, { data, loading }] = useLazyQuery(
    GET_CANDIDATE_REFERENCES
  );

  useEffect(() => {
    if (candidateId && candidate) {
      getReference(candidateId);
      editPermission(candidate);
    }

    // eslint-disable-next-line
  }, [candidateId, candidate, currentPage, limit]);

  const getReference = (candidateId: string) => {
    getReferences({
      variables: { candidateId, page: currentPage, limit },
    });
  };

  const editPermission = (candidate: {
    shareWith: { id: string };
    recruiterOwner: { id: string };
  }) => {
    if (
      userRoles?.includes("ADMIN") ||
      userRoles?.includes("EXECUTIVE") ||
      candidate?.shareWith?.id === user?.id
    ) {
      setEditPermissions(false);
      return;
    } else if (user?.id === candidate?.recruiterOwner?.id) {
      setEditPermissions(false);
      return;
    } else if (candidate?.recruiterOwner === null) {
      setEditPermissions(false);
      return;
    } else {
      setEditPermissions(true);
      return;
    }
  };

  const viewToggle = () => {
    setIsView(!isView)
    !!editReferenceId && setEditReferenceId("")
  }

  const toggle = () => {
    setModal(!modal)
    !!editReferenceId && setEditReferenceId("")
  }

  const handleView = (item: any) => {
    setReferenceData(item);
    setEditReferenceId(item?.id);
    setIsView(true)
  }

  // const handleView = async (item: { id: string; referenceName: string }) => {
  //   setViewId(item?.id);
  //   setViewLoading(true);
  //   try {
  //     const response = await axios.get(
  //       `${process.env.REACT_APP_API_BASE_URL || "http://localhost:3001"
  //       }/candidate-pipelining-reference/downloadreference/${item?.id}`,
  //       {
  //         responseType: "blob",
  //       }
  //     );

  //     const file = new Blob([response.data], { type: "application/pdf" });
  //     const fileURL = URL.createObjectURL(file);

  //     if (fileURL) {
  //       setViewLoading(false);
  //       setBlobFileUrl(fileURL);
  //       setBlobFileName(`${item?.referenceName}.pdf`);
  //       togglePreview();
  //     }
  //   } catch (error) {
  //     toast.error("Error while previewing file..");
  //   }
  // };

  const confirmAction = async () => {
    try {
      const response = await deleteReference({
        variables: { referenceId: deleteReferenceId },
      });
      if (response?.data) {
        toast.success("Successfully deleted!");
        setReferenceDeleteConfirm(false);
        getReferences({
          variables: { candidateId, page: currentPage, limit },
        });
      } else {
        setReferenceDeleteConfirm(false);
      }
    } catch (error: any) {
      // e.g. 409 when the reference is managed by the AI Reference Taker.
      toast.error(error?.message || "Could not delete the reference.");
      setReferenceDeleteConfirm(false);
    }
  };

  const references = data?.allReferenceList?.references ?? [];
  const count = data?.allReferenceList?.count ?? 0;

  // After a delete empties the last page, step back to the new last page so
  // the count text and rows stay consistent (the footer hides Prev/Next once
  // everything fits on one page, which would otherwise strand the user).
  useEffect(() => {
    if (!count) return;
    const lastPage = Math.max(1, Math.ceil(count / limit));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [count, limit, currentPage]);

  return (
    <div className="refSection">
      <RefSectionHeader
        title="Reference Forms"
        count={count}
        description="Completed and in-progress reference responses"
        action={
          <button
            type="button"
            className="refBtn refBtnBrandOutline"
            onClick={() => {
              setReferenceData({});
              setEditReferenceId("");
              setModal(true);
            }}
            disabled={editPermissions}
          >
            + Add Reference
          </button>
        }
      />

      <div className="refTableScroll">
        <div className="refTable">
          <div role="table" aria-label="Reference forms">
          <RefTableHead columns={REFERENCE_FORM_COLUMNS} />

          {loading && !references.length ? (
            <div className="refLoadingRow">
              <Loader loaderClass="sm-height " />
            </div>
          ) : null}

          {references.map((item) => (
            <div key={item.id} className="refGridRow refBodyRow" role="row">
              <div role="cell">
                <RefNameCell
                  name={item.referenceName}
                  title={item.title}
                  company={item.company}
                  relationship={item.relationshipToCandidate}
                />
              </div>
              <div role="cell">
                <RefContactCell email={item.email} phone={item.phoneNumber} />
              </div>
              <div role="cell">
                <FormStatusBadge status={item.status} />
              </div>
              <div role="cell">
                <RefDateCell value={item.updatedAt} />
              </div>
              <div role="cell" className="refActionCell">
                <RefIconButton label="View" onClick={() => handleView(item)}>
                  <EyeIcon />
                </RefIconButton>
                <RefIconButton
                  label="Edit"
                  onClick={() => {
                    setReferenceData(item);
                    setEditReferenceId(item?.id);
                    setModal(true);
                  }}
                  disabled={editPermissions}
                >
                  <PenIcon />
                </RefIconButton>
                <DownloadReference item={item} />
                <RefIconButton
                  label="Delete"
                  danger
                  onClick={() => {
                    setReferenceDeleteConfirm(true);
                    setDeleteReferenceId(item?.id);
                  }}
                  disabled={editPermissions}
                >
                  <TrashIcon />
                </RefIconButton>
              </div>
            </div>
          ))}

          {!loading && !references.length && (
            <RefEmptyRow text="No reference forms" />
          )}
          </div>

          <RefFooter
            page={currentPage}
            pageSize={limit}
            total={count}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setCurrentPage(1);
              setLimit(size);
            }}
          />
        </div>
      </div>

      <ConfirmPopUp
        confirmText="Are you sure you want to delete this Reference from Reference list?"
        isOpen={actionConfirm}
        toggle={setReferenceDeleteConfirm}
        confirmAction={confirmAction}
        modalHeading="Delete Action"
        btnText="Delete"
        btnColor="primary"
        className="revampDialog revampDialogWidth"
      />

      <ViewReferenceModal
        isOpen={isView}
        toggle={viewToggle}
        modalHeader={"View Reference"}
        referenceData={referenceData}
      />

      <AddReferenceModal
        isOpen={modal}
        toggle={toggle}
        candidateId={candidateId}
        refetch={() => getReference(candidateId)}
        referenceId={editReferenceId}
        modalHeader={editReferenceId ? "Update Reference" : "Add Reference"}
        referenceData={referenceData}
      />
    </div>
  );
};

export default ReferenceListing;
